import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Background,
  Pose,
} from '../components/v3/products/regular/subcomponents/BackgroundsPosesSelector/types';
import {
  backgroundConfigurationRequiredSelector,
  poseConfigurationRequiredSelector,
  selectFirstFillablePoseSlot,
  selectPoseSlotsFillable,
} from '../selectors';
import {
  getProductOptionsDefaultsKey,
  reconcileProductOptions,
} from '../components/v3/products/regular/subcomponents/productOptionsHelpers';
import {
  backgroundSlotsCount,
  productPosesCount,
} from '../components/v3/products/imageRecords';
import type { PerPosePose } from '../components/v3/products/perPoseBackgrounds';

interface CropOption {
  id: number;
  [key: string]: unknown;
}

interface ProductConfigState {
  configurationOrderKey: string | null;
  currentProduct: any | null;
  backgroundSelections: Background[];
  poseSelections: Pose[];
  poseSelectionIds: number[];
  cropSelections: (CropOption | null)[];
  personalizationValue: string | null;
  personalizationOffered: boolean;
  selectedProductOptionKey: string | null;
  selectedProductOptions: Record<string, string>;
  productOptionsDefaultsInitializedFor: string | null;
  activeConfigSlotIndex: number | null;
  activeConfigStep: 'pose' | 'background' | null;
  backgroundsPosesSelectorVisible: boolean;
  editMode: boolean;
}

const initialState: ProductConfigState = {
  configurationOrderKey: null,
  currentProduct: null,
  backgroundSelections: [],
  poseSelections: [],
  poseSelectionIds: [],
  cropSelections: [],
  personalizationValue: null,
  personalizationOffered: false,
  selectedProductOptionKey: null,
  selectedProductOptions: {},
  productOptionsDefaultsInitializedFor: null,
  activeConfigSlotIndex: null,
  activeConfigStep: null,
  backgroundsPosesSelectorVisible: false,
  editMode: false,
};

const productConfigSlice = createSlice({
  name: 'productConfig',
  initialState,
  reducers: {
    // `fixedPoses` seeds the pose slots of a "background chosen per pose"
    // product (perPoseBackgrounds, ENG-3723): its slots ARE the gallery's poses,
    // so they arrive filled and the parent picks backgrounds only. Background
    // picks survive a re-begin only while the poses are the same, in the same
    // order — each pick belongs to the pose at its index.
    beginProductConfiguration(
      state,
      action: PayloadAction<{
        product: any;
        orderKey?: string | null;
        fixedPoses?: PerPosePose[] | null;
      }>,
    ) {
      const sameDestination =
        state.currentProduct?.id === action.payload.product.id &&
        state.configurationOrderKey === (action.payload.orderKey ?? null);
      const selectedProductOptions = reconcileProductOptions(
        action.payload.product,
        sameDestination ? state.selectedProductOptions : {},
      );
      const mirror = sameDestination ? state.selectedProductOptionKey : null;
      const { fixedPoses } = action.payload;
      const fixedPoseIds = (fixedPoses ?? []).map((pose) => pose.id);
      const samePoses =
        sameDestination &&
        fixedPoseIds.length === state.poseSelectionIds.length &&
        fixedPoseIds.every((id, index) => state.poseSelectionIds[index] === id);
      const fixedPoseState = fixedPoses
        ? {
            poseSelections: fixedPoses,
            poseSelectionIds: fixedPoseIds,
            backgroundSelections: samePoses ? state.backgroundSelections : [],
          }
        : {};
      return {
        ...(sameDestination ? state : initialState),
        ...fixedPoseState,
        currentProduct: action.payload.product,
        configurationOrderKey: action.payload.orderKey ?? null,
        selectedProductOptions,
        selectedProductOptionKey:
          mirror && Object.values(selectedProductOptions).includes(mirror)
            ? mirror
            : null,
        productOptionsDefaultsInitializedFor: getProductOptionsDefaultsKey(
          action.payload.product,
        ),
      };
    },
    endProductConfiguration() {
      return initialState;
    },
    setCurrentProduct(state, action: PayloadAction<any>) {
      state.configurationOrderKey = null;
      state.currentProduct = action.payload;

      // Seed/reconcile the selected product options for the mounted product.
      // Keyed by the product's option-set signature so a re-dispatch for the
      // same product preserves valid selections while healing a stale legacy
      // mirror, and a genuine product (or option-set) change drops stale
      // selections and fills required defaults.
      const defaultsKey = getProductOptionsDefaultsKey(action.payload);
      const clearLegacyProductOptionKeyIfStale = () => {
        if (
          !Object.values(state.selectedProductOptions).includes(
            state.selectedProductOptionKey as string,
          )
        ) {
          state.selectedProductOptionKey = null;
        }
      };

      if (!defaultsKey) return;
      if (state.productOptionsDefaultsInitializedFor === defaultsKey) {
        clearLegacyProductOptionKeyIfStale();
        return;
      }
      state.selectedProductOptions = reconcileProductOptions(
        action.payload,
        state.selectedProductOptions,
      );
      state.productOptionsDefaultsInitializedFor = defaultsKey;
      // selectedProductOptionKey is a legacy single-value mirror of the last
      // pick in the map above, and configureCartItem falls back to it whenever
      // the map reconciles empty (utils/cart.js, the `else if (productOptionKey)`
      // branch). Left unreconciled it outlives the entry it mirrors: a pick
      // dropped by the product switch silently re-applied itself — priced —
      // to the next product, under the generic "Product Options" label, while
      // the dropdown (which reads the map) correctly showed "None", and then
      // 422'd at submit on its 'legacy' optionSetKey (ENG-3051).
      clearLegacyProductOptionKeyIfStale();
    },
    setBackgroundSelections(state, action: PayloadAction<Background[]>) {
      state.backgroundSelections = action.payload;
    },
    setPoseSelections(state, action: PayloadAction<Pose[]>) {
      state.poseSelections = action.payload;
    },
    setPoseSelectionIds(state, action: PayloadAction<number[]>) {
      state.poseSelectionIds = action.payload;
    },
    setCropSelections(state, action: PayloadAction<(CropOption | null)[]>) {
      state.cropSelections = action.payload;
    },
    setPersonalizationValue(state, action: PayloadAction<string | null>) {
      state.personalizationValue = action.payload;
    },
    setPersonalizationOffered(state, action: PayloadAction<boolean>) {
      state.personalizationOffered = action.payload;
    },
    setSelectedProductOptions(
      state,
      action: PayloadAction<{
        optionSetKey: string;
        optionValue: string;
      }>,
    ) {
      state.selectedProductOptions[action.payload.optionSetKey] =
        action.payload.optionValue;
      state.selectedProductOptionKey = action.payload.optionValue;
    },
    // The active slot and the panel's visibility always move together:
    // seeding a slot without opening the panel (or vice versa) is how the
    // mobile incentive dead-end happened, so the pairing is enforced here
    // rather than trusted to every call site.
    openConfigSlot(
      state,
      action: PayloadAction<{
        slotIndex: number;
        step: 'pose' | 'background';
      }>,
    ) {
      state.activeConfigSlotIndex = action.payload.slotIndex;
      state.activeConfigStep = action.payload.step;
      state.backgroundsPosesSelectorVisible = true;
    },
    // Used both when configuration completes and when the parent closes the
    // panel (mobile toggle): closing always clears the active slot — every
    // reopen path reseeds via pickNextConfigSlot.
    closeConfigPanel(state) {
      state.activeConfigSlotIndex = null;
      state.activeConfigStep = null;
      state.backgroundsPosesSelectorVisible = false;
    },
    setEditMode(state, action: PayloadAction<boolean>) {
      state.editMode = action.payload;
    },
    resetConfig(state) {
      state.backgroundSelections = [];
      state.poseSelections = [];
      state.poseSelectionIds = [];
      state.cropSelections = [];
      state.activeConfigSlotIndex = null;
      state.activeConfigStep = null;
      state.backgroundsPosesSelectorVisible = false;
      // The SMS phone-number edit form must not stay open across a product
      // change (it was component-local before the Redux refactor and reset
      // on unmount).
      state.editMode = false;
    },
  },
});

export const {
  beginProductConfiguration,
  endProductConfiguration,
  setCurrentProduct,
  setBackgroundSelections,
  openConfigSlot,
  setCropSelections,
  setEditMode,
  setPersonalizationOffered,
  setPersonalizationValue,
  setPoseSelectionIds,
  setPoseSelections,
  setSelectedProductOptions,
  resetConfig,
} = productConfigSlice.actions;

// Closing is never dispatched from outside: the picker closes through
// togglePosesBackgroundsSelector (the manual close) or advanceConfigSlot
// (configuration complete), both below.
const { closeConfigPanel } = productConfigSlice.actions;

// ── Configuration lifecycle ────────────────────────────────────────────────
//
// A product's configuration runs through the poses/backgrounds picker panel,
// driven entirely by this slice:
//
// 1. Product change — the regular config screen atomically begins the product
//    and destination context, clearing selections and seeding option defaults,
//    then dispatches autoOpenConfigPanel on
//    desktop viewports. On mobile every product starts closed behind the
//    SelectedPosesAndBackgrounds "Tap to configure" entry point.
//
// 2. Opening — three entry points, all funnelling into the openConfigSlot
//    reducer (which pairs the active slot, step, and panel visibility
//    atomically — see its comment):
//    • autoOpenConfigPanel — desktop-only, right after the product change.
//    • togglePosesBackgroundsSelector — the mobile tile tap; also the only
//      manual close (closeConfigPanel).
//    • a plain openConfigSlot dispatch — the edit taps on a specific tile
//      (PoseSlot / BackgroundSlot).
//
// 3. Filling — after storing a selection, PosesConfig / BackgroundOption
//    dispatch advanceConfigSlot({slotIndex, step}): the picker moves to the
//    next unfilled slot, or the panel closes (closeConfigPanel) when
//    configuration is complete. The incentive flow seeds its initial slot
//    through the same thunk with no lastFilled.
//
// 4. Gating — selectInitialStepsComplete / selectCanBeSubmitted (in
//    selectors/productConfig) decide when Add to Cart unlocks; the done
//    message and SMS confirmation follow from them.
//
// pickNextConfigSlot below is the navigator the thunks share. It is private
// to this slice — everything outside acts through the thunks above.

// Decide which (slot, step) the picker should focus on next for the product
// being configured (productConfig.currentProduct), given the current
// selections.
//
// Poses and backgrounds are INDEPENDENT totals — a product can have any number
// of poses (one pose slot per image node) and any number of backgrounds
// (`background_options_count`), and the two counts need not match (e.g. one pose
// with two backgrounds, or three poses with one background). Each step's bounds
// are therefore checked against its own count. The interleave runs pose 0 →
// bg 0 → pose 1 → bg 1 → … and then drains whichever list is longer.
//
// Step availability comes from the shared selectors: prepay hides every pose
// step (poseConfigurationRequiredSelector), and a skip-background digital
// download or an empty shoot background list hides every background step
// (backgroundConfigurationRequiredSelector).
//
// `lastFilled` is interaction context (the {slotIndex, step} just filled by
// the customer), not Redux state — hence the extra argument.
const pickNextConfigSlot = (
  state: any,
  lastFilled: { slotIndex: number; step: 'pose' | 'background' } | null = null,
): { slotIndex: number; step: 'pose' | 'background' } | null => {
  const product = state.productConfig?.currentProduct;
  const poseSelections = state.productConfig?.poseSelections ?? [];
  const backgroundSelections = state.productConfig?.backgroundSelections ?? [];

  const posesCount = productPosesCount(product);
  const productBackgroundSlotsCount = backgroundSlotsCount(product);

  const poseStepAvailable = poseConfigurationRequiredSelector(state);
  const backgroundStepAvailable =
    backgroundConfigurationRequiredSelector(state);

  // A pose slot is only worth focusing if its own source has images: a group
  // slot with no class photo, or an individual slot on a gallery with no
  // individual poses, would open an empty picker. Same rule the gate uses.
  const poseSlotsFillable = selectPoseSlotsFillable(state);
  const poseSlotNeedsFilling = (index: number): boolean =>
    poseSlotsFillable[index] && !poseSelections[index];

  if (lastFilled) {
    const { slotIndex, step } = lastFilled;

    if (
      step === 'pose' &&
      backgroundStepAvailable &&
      slotIndex < productBackgroundSlotsCount &&
      !backgroundSelections[slotIndex]
    ) {
      return { slotIndex, step: 'background' };
    }

    if (
      step === 'background' &&
      poseStepAvailable &&
      slotIndex < posesCount &&
      poseSlotNeedsFilling(slotIndex)
    ) {
      return { slotIndex, step: 'pose' };
    }
  }

  if (poseStepAvailable) {
    for (let i = 0; i < posesCount; i++) {
      if (poseSlotNeedsFilling(i)) {
        return { slotIndex: i, step: 'pose' };
      }
    }
  }

  if (backgroundStepAvailable) {
    for (let i = 0; i < productBackgroundSlotsCount; i++) {
      if (!backgroundSelections[i]) {
        return { slotIndex: i, step: 'background' };
      }
    }
  }

  return null;
};

// Moves the picker onward after a slot is filled (`lastFilled` is the
// {slotIndex, step} the customer just completed): the next unfilled slot if
// one remains, otherwise the panel closes — configuration is done. Also seeds
// the initial slot for the incentive flow (no lastFilled, nothing selected
// post-reset): with nothing to configure the close is a no-op and the panel
// stays closed, keeping Add to Cart immediately reachable.
export const advanceConfigSlot =
  (
    lastFilled: {
      slotIndex: number;
      step: 'pose' | 'background';
    } | null = null,
  ) =>
  (dispatch: (action: unknown) => void, getState: () => any) => {
    const next = pickNextConfigSlot(getState(), lastFilled);
    if (next) {
      dispatch(
        openConfigSlot({
          slotIndex: next.slotIndex,
          step: next.step as 'pose' | 'background',
        }),
      );
      return;
    }
    dispatch(closeConfigPanel());
  };

// The mobile "Tap to configure" toggle: closes the picker when visible
// (clearing the active slot too — every reopen reseeds), otherwise opens it
// for the product being configured (productConfig.currentProduct — synced
// from the URL-derived product by the config screens), seeding the next
// unfilled slot via pickNextConfigSlot and falling back to slot 0 when every
// slot is filled (the user is reopening to review/edit). Opening a specific
// slot (the edit-selection taps) is a plain openConfigSlot dispatch instead.
export const togglePosesBackgroundsSelector =
  () => (dispatch: (action: unknown) => void, getState: () => any) => {
    const state = getState();

    if (state.productConfig.backgroundsPosesSelectorVisible) {
      dispatch(closeConfigPanel());
      return;
    }

    if (!state.productConfig.currentProduct) return;

    const next = pickNextConfigSlot(state);

    if (next) {
      dispatch(
        openConfigSlot({
          slotIndex: next.slotIndex,
          step: next.step as 'pose' | 'background',
        }),
      );
      return;
    }

    // The navigator found no slot to fill: either everything is filled (the user
    // is reopening to review/edit) or nothing can be filled at all. Prefer the
    // first FILLABLE slot so a review lands somewhere with content — but still
    // open the pose step when nothing is fillable, because PosesConfig is what
    // explains WHICH photo the gallery is missing (NoPoseImageMessage). Falling
    // through to a background step there would open an empty panel and leave the
    // blocked Add to Cart unexplained.
    const poseStep = poseConfigurationRequiredSelector(state);
    const firstFillable = selectFirstFillablePoseSlot(state);

    dispatch(
      openConfigSlot({
        slotIndex: poseStep ? firstFillable ?? 0 : 0,
        step: poseStep ? 'pose' : 'background',
      }),
    );
  };

// Auto-opens the config panel (hidden by default) for the product just set —
// the desktop flow, dispatched right after beginProductConfiguration on a
// product change (RegularProductConfig gates it on viewport width; on mobile
// every product starts closed behind the "Tap to configure" entry point).
// Running only against that post-reset state is also what keeps a finished
// picker from reopening — there is no mid-configuration caller.
export const autoOpenConfigPanel =
  () => (dispatch: (action: unknown) => void, getState: () => any) => {
    const state = getState();
    if (!state.productConfig.currentProduct) return;

    // The navigator already encodes every condition auto-open needs: prepay
    // hides pose steps (poseConfigurationRequiredSelector), a shoot with no
    // catalogue or a skip-background digital download hides background steps
    // (backgroundConfigurationRequiredSelector), and a slot whose own source
    // has no images is skipped rather than opened empty
    // (selectPoseSlotsFillable). It runs against post-reset state here, so the
    // slot it picks is the first one the customer can act on — which is why
    // this no longer hardcodes slot 0: on a gallery with only a class photo,
    // the first fillable slot of a mixed product is the group node, not slot 0.
    //
    // Nothing to configure => nothing opens, and Add to Cart is immediately
    // reachable.
    const next = pickNextConfigSlot(state);
    if (!next) return;

    dispatch(openConfigSlot({ slotIndex: next.slotIndex, step: next.step }));
  };

export default productConfigSlice.reducer;
